/**
 * Donut — token-themed donut/pie chart (recharts wrapper) with an optional
 * center label (e.g. a total). Renders its OWN legend (not recharts') so the
 * ring stays perfectly centered in its box and the center label aligns dead-on.
 */
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import type { ReactNode } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { seriesColor } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';
import styles from './Charts.module.css';

export interface DonutDatum {
  name: string;
  value: number;
  color?: string;
}

export interface DonutProps {
  data: DonutDatum[];
  /** Pixel height of the ring area (legend sits beside/below it). Default 220. */
  height?: number;
  /** Ring thickness as a fraction of the radius (0.1–0.9). Default 0.42. */
  thickness?: number;
  /** Centered content (e.g. a total + caption). Rendered over the ring. */
  centerLabel?: ReactNode;
  valueFormatter?: (value: number | string | undefined) => string;
  /** Render the built-in legend (custom, token-styled). Default true. */
  showLegend?: boolean;
  /** Legend placement relative to the ring. Default 'right'. */
  legendPosition?: 'right' | 'bottom';
  /** Required: short text description for screen readers. */
  ariaLabel: string;
  className?: string;
}

export function Donut({
  data,
  height = 220,
  thickness = 0.42,
  centerLabel,
  valueFormatter,
  showLegend = true,
  legendPosition = 'right',
  ariaLabel,
  className,
}: DonutProps) {
  const reduced = usePrefersReducedMotion();
  const outer = Math.round((height / 2) * 0.92);
  const inner = Math.round(outer * (1 - Math.min(Math.max(thickness, 0.1), 0.9)));
  const fmt = valueFormatter ?? ((v: number | string | undefined) => (typeof v === 'number' ? v.toLocaleString() : String(v ?? '')));

  return (
    <figure
      role="img"
      aria-label={ariaLabel}
      className={[styles.donutWrap, legendPosition === 'bottom' ? styles.donutCol : '', className]
        .filter(Boolean)
        .join(' ')}
    >
      <div className={styles.donutChart} style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={inner}
              outerRadius={outer}
              paddingAngle={1.5}
              stroke="var(--color-bg-surface-elevated, #FFFFFF)"
              strokeWidth={2}
              isAnimationActive={!reduced}
            >
              {data.map((d, i) => (
                <Cell key={d.name} fill={d.color ?? seriesColor(i)} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip valueFormatter={valueFormatter} hideLabel />} />
          </PieChart>
        </ResponsiveContainer>
        {centerLabel != null && <div className={styles.donutCenter}>{centerLabel}</div>}
      </div>

      {showLegend && (
        <ul className={styles.donutLegend}>
          {data.map((d, i) => (
            <li key={d.name} className={styles.donutLegendItem}>
              <span className={styles.tooltipSwatch} style={{ background: d.color ?? seriesColor(i) }} />
              <span className={styles.donutLegendName}>{d.name}</span>
              <span className={styles.donutLegendValue}>{fmt(d.value)}</span>
            </li>
          ))}
        </ul>
      )}
    </figure>
  );
}
